import cx from 'classnames';
import { Space } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { RootState } from '../../../../state/store';
import Icon from '../../../../common/components/Icon/Icon';
import { THEME, weightUnits } from '../../../../config/commons';
import * as weighingTransfersSlice from '../weighingTransfersSlice';
import { GenericParam } from '../../../../common/interfaces/commons';
import { LrvText } from '../../../../common/components/LrvText/LrvText';
import { Parameter, ParameterDefault, TransferMetric, TransferTransport, Tray } from '../interfaces';
import { calculateTransferMetrics, defaultCapsuleParameters, defaultPumpParameters, getFormattedParameterValue, getParameterLabel, showErrorParameterValue, transportTypes } from '../../../../helpers/transfer-stocking';

import styles from './Transport.module.scss';
import { TransportAccordion } from './TransportAccordion';

interface Props {
  transport: TransferTransport;
  maxSmallAnimalsWeight: number;
  maxSmallAnimalsPercentage: number;
  theme?: 'light' | 'dark';
}

export const Transport = (props: Props) => {
  const { transport, maxSmallAnimalsWeight, maxSmallAnimalsPercentage, theme = 'dark' } = props;
  
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const isLightTheme = theme === THEME.LIGHT;

  const { trays, parameters, transports, selectedStocking } = useSelector((state: RootState) => state.weighingTransfers);
  
  const [traysFiltered, setTraysFiltered] = useState<Tray[]>([]);
  const [transferMetric, setTransferMetric] = useState<TransferMetric>({ animalsPerCubicMeter: 0, animalsTransferred: 0, averageWeight: 0, smallAnimalsPercentage: 0, uniformityWeight: 0, variationCoefficientWeight: 0 });

  /*useEffect section*/
  useEffect(() => {
    const traysFiltered = trays.filter((tray) => tray.transportId === transport.transportId && tray.transferTransportId === transport._id);
    setTraysFiltered(traysFiltered);
  }, [trays, transport?._id]);

  useEffect(() => {
    const selectedTransport = transports.find((item) => item._id == transport.transportId);
    const transferMetric = calculateTransferMetrics({ trays: traysFiltered, maxSmallAnimalsWeight, volume: selectedTransport?.capacity });
    setTransferMetric(transferMetric);
  }, [traysFiltered, maxSmallAnimalsWeight, transports, transport.transportId]);
  /*End of useEffect section*/

  /*render components section */
  const renderParameter = (parameter: ParameterDefault) => {
    const showError = showErrorParameterValue({ parameter, smallAnimalsPercentage: transferMetric.smallAnimalsPercentage, maxSmallAnimalsPercentage });
    const isStockingDestination = parameter.key === 'stockingDestination';

    return (
      <div>
        <div className={styles.containerValue}>
          <LrvText theme={theme} text={getFormattedParameterValue({ parameter, trays: traysFiltered, transferMetric })} className={showError ? styles.error : ''}/>
        </div>

        <div className={styles.containerLabel}>
          <LrvText theme={theme} text={getParameterLabel({ parameter, maxSmallAnimalsWeight })} className={cx(styles.label, isLightTheme ? styles.lightLabel : styles.darkLabel)}/>
          {isStockingDestination && <>
            &nbsp;
            <div
              onClick={() => {
                const stockingDestination = (parameter as Parameter).stocking as GenericParam;
                const campusId = stockingDestination.campusId;
                const moduleId = stockingDestination.moduleId;
                const tankId = stockingDestination.tankId;
                const code = stockingDestination.code;

                
                dispatch(weighingTransfersSlice.fetchStockingDestination({ campusId, moduleId, tankId, code }));
                dispatch(weighingTransfersSlice.setShowStockingDestinationModal(true));
              }}
            >
              <Icon name='information' theme={theme} className={styles.icon}/>
            </div>
          </>
          }
        </div>
      </div>
    );
  };

  const renderParameters = () => {
    const { transport } = props;

    const items = transport.transportType === transportTypes.CAPSULE ? defaultCapsuleParameters : defaultPumpParameters;
    const filteredParameters = parameters.filter((param) => !items.some((item) => item.key === param.key) && param.transferTransportId === transport._id);
    const allParameters = [...items, ...filteredParameters];

    return (
      <div className={styles.parameters}>
        <div className={styles.containerTitle}>
          <LrvText theme={theme} text={t('stockings.parameters')} className={styles.title}/>
        </div>

        <div className={styles.items}>
          {allParameters.map(renderParameter)}
        </div>
      </div>
    );
  };

  const renderTray = (props: {tray: Tray; index: number}) => {
    const { tray, index } = props;

    return (
      <div className={cx(styles.containerTray, isLightTheme ? styles.lightContainerTray : styles.darkContainerTray)}>
        <div>
          <LrvText theme={theme} text={`${t('stockings.transfers.tray')} ${index + 1}`} className={styles.trayTitle}/>
        </div>

        <div>
          <LrvText theme={theme} text={`${tray.trayWeight} ${weightUnits.KG}`} className={styles.trayWeight} />
        </div>

        <div
          onClick={async () => {
            if (!tray?._id || !selectedStocking?._id || !tray?.averageWeight) {
              return;
            }

            await weighingTransfersSlice.fetchAnalysisByTray({ transferId: tray.transferId, trayId: tray._id, companyId: tray.companyId, stockingId: selectedStocking._id });
          }}
        >
          <div>
            <LrvText
              theme={theme}
              text={t('stockings.transfers.sample')}
              className={cx(styles.viewAnalysis, isLightTheme ? styles.lightViewAnalysis : styles.darkViewAnalysis, tray.averageWeight ? '' : styles.hide)}
            />
            &nbsp;
            <Icon name='external-link' theme={theme} className={cx(isLightTheme ? styles.lightViewAnalysis : styles.darkViewAnalysis, tray.averageWeight ? '' : styles.hide)}/>
          </div>
        </div>
      </div>
    );
  };

  const renderTrays = () => {
    return (
      <div className={styles.trays}>
        <div>
          <LrvText theme={theme} text={t('stockings.transfers.trays')} className={styles.title}/>
        </div>

        <Space className={styles.items} size={24} wrap>
          {traysFiltered.map((tray, index) => renderTray({ tray, index }))}
        </Space>
      </div>
    );
  };
  /* End of render components section */

  return (
    <div className={styles.transfer}>
      <TransportAccordion
        title={
          <div className={styles.containerTransportName}>
            <LrvText theme={theme} text={t('stockings.transfers.transport')} className={styles.transportName}/>
            &nbsp;
            <LrvText theme={theme} text={transport.transportName} className={cx(styles.transportName, styles.bold)}/>
          </div>
        }
        theme={theme}
      >
        <div className={cx(styles.container, isLightTheme ? styles.lightContainer : styles.darkContainer)}>
          {renderParameters()}
          {renderTrays()}
        </div>
      </TransportAccordion>
    </div>
  );
};
